import { Box, Button, Heading, Text, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { TextAreaBuild } from 'components/molecules/TextAreaBuild'
import { useInstitutionsContext } from 'contexts/institutions'
import { Controller, useForm } from 'react-hook-form'
import { createNotification } from 'services/notifications'
import formatter from 'utils/formatter'
import { formNotifications } from './form'
import { schema } from './schema'
import { IFormNotification, TFormNotificationsFields } from './types'

const FormNewNotifications = () => {
    const toast = useToast()
    const {
        handleSubmit,
        register,
        watch,
        control,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<IFormNotification>({
        resolver: yupResolver(schema)
    })

    const initialForm = {
        titulo: '',
        descricao: '',
        perfilId: null,
        dataExpiracao: ''
    }

    const { userPerfis, currentInstitution, getInstitutionsPerfis } =
        useInstitutionsContext()
    const form = formNotifications(
        userPerfis.filter(data => data.perfil !== 'Usuário Admin da IF')
    )

    const buildPayload = (data: IFormNotification) => {
        return {
            titulo: data?.titulo,
            descricao: data?.descricao,
            perfilId: data?.perfilId ?? null,
            guidIf: currentInstitution?.id ?? null,
            dataExpiracao: data?.dataExpiracao
                ? formatter.timeConverter(data.dataExpiracao)
                : null
        }
    }

    const onSubmit = async (data: IFormNotification) => {
        try {
            await createNotification(buildPayload(data))
            toast({
                title: 'Enviar notificação',
                description: 'Notificação enviada com sucesso!',
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })

            handleClear()
        } catch (e) {
            toast({
                title: 'Erro ao enviar notificação',
                description: 'Tente novamente mais tarde',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        }
    }

    const handleClear = () => {
        reset(initialForm, { keepDirtyValues: false })
    }

    return (
        <Layout
            type="row"
            display="flex"
            justifyContent="center"
            alignItems="center"
            w="100%"
        >
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                w="100%"
                maxW="3xl"
                gap={8}
            >
                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading size="md" textAlign="center" mb="2">
                        Notificação
                    </Heading>
                    <Text textAlign="center">
                        Preencha os campos a seguir para enviar notificações aos
                        usuários da plataforma.
                    </Text>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout type="container" full px={6} mt={8}>
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {form.map(({ layout, input, select }, key) => (
                                    <Layout {...layout} key={key}>
                                        {select?.id ? (
                                            <SelectBuild
                                                {...select}
                                                err={
                                                    errors[
                                                        select.id as TFormNotificationsFields
                                                    ]
                                                }
                                                {...register(
                                                    select.id as TFormNotificationsFields
                                                )}
                                            />
                                        ) : input?.id === 'descricao' ? (
                                            <Layout
                                                type="col"
                                                layout={{ base: 12 }}
                                            >
                                                <Controller
                                                    name="descricao"
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <TextAreaBuild
                                                            label="Descrição"
                                                            id="descricao"
                                                            maxLength={500}
                                                            placeholder="Descreva a descrição"
                                                            {...field}
                                                            required
                                                            err={
                                                                errors[
                                                                    'descricao'
                                                                ] as any
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Layout>
                                        ) : (
                                            input && (
                                                <InputBuild
                                                    {...input}
                                                    id={input.id}
                                                    value={
                                                        watch(
                                                            input.id as any
                                                        ) as any
                                                    }
                                                    err={
                                                        errors[
                                                            input.id as TFormNotificationsFields
                                                        ]
                                                    }
                                                    {...register(
                                                        input.id as TFormNotificationsFields
                                                    )}
                                                    required={input.required}
                                                />
                                            )
                                        )}
                                    </Layout>
                                ))}

                                <Layout
                                    type="col"
                                    layout={{ base: 12, md: 12 }}
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    gap="16px"
                                >
                                    <Button
                                        size="md"
                                        w={{ md: '200px', sm: 'full' }}
                                        colorScheme="primary"
                                        variant="outline"
                                        onClick={handleClear}
                                    >
                                        Limpar campos
                                    </Button>
                                    <Button
                                        size="md"
                                        w={{ md: '200px', sm: 'full' }}
                                        colorScheme="primary"
                                        type="submit"
                                        isLoading={isSubmitting}
                                    >
                                        Enviar
                                    </Button>
                                </Layout>
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Layout>
    )
}

export default FormNewNotifications
